// ============================================================
// Raporlar (§6) — yönetimin karar alma ekranı
//
// Panel günlük operasyonu gösterir; burası eğilim ve risk gösterir.
// ============================================================

import { card, classChip, emptyNote, sectionTitle } from "../components/ui.js";
import { DONEM } from "../config.js";
import { B } from "../domain/context.js";
import { avatar } from "../lib/avatar.js";
import { donemAdi, esc, money } from "../lib/fmt.js";
import {
  analitikOzeti,
  bakiyeYaslandirma,
  bosKontenjanMaliyeti,
  devamlilik,
  devamsizlikDeseni,
  dolulukTrendi,
  huniDonusumu,
  oranUyumu,
  tahsilatTrendi,
} from "../domain/analytics.js";

export function viewRaporlar() {
  const bid = B().id;
  const o = analitikOzeti(bid);

  return `
  <div class="grid gap-3 sm:gap-4 grid-cols-2 xl:grid-cols-4 mb-4">
    ${kutu("Doluluk", `%${o.sonDoluluk.oran}`,
      `${o.sonDoluluk.kayitli}/${o.sonDoluluk.kapasite} · ${o.dolulukDegisim >= 0 ? "+" : ""}${o.dolulukDegisim} puan`, "teal")}
    ${kutu("Boş Kontenjan Maliyeti", money(o.kayipGelir), "aylık kaçırılan gelir", "sun")}
    ${kutu("Oran Riski", o.oranIhlali || o.kapasiteRiski,
      o.oranIhlali ? `${o.oranIhlali} sınıfta ihlal` : `${o.kapasiteRiski} sınıfta kapasite riski`,
      o.oranIhlali ? "coral" : "sun")}
    ${kutu("Düşük Devam", o.dusukDevam, "%80 altı öğrenci", "coral")}
  </div>

  <div class="grid gap-4 lg:grid-cols-2 items-start">
    ${oranKarti(bid)}
    ${dolulukKarti(bid)}
    ${tahsilatKarti(bid)}
    ${yaslandirmaKarti(bid)}
    ${huniKarti(bid)}
    ${devamsizlikKarti(bid)}
    ${devamlilikKarti(bid)}
  </div>`;
}

const kutu = (label, value, sub, color) => card(`
  <div class="p-4">
    <div class="text-xs font-semibold text-inksoft">${label}</div>
    <div class="font-display font-extrabold text-2xl mt-1 text-${color} break-words">${value}</div>
    <div class="text-[11px] text-inksoft/80 mt-0.5">${sub}</div>
  </div>`, "min-w-0");

// ---------- Öğretmen / çocuk oranı ----------
function oranKarti(bid) {
  const list = oranUyumu(bid);
  const sorunlu = list.filter(x => x.ihlal || x.kapasiteRiski);

  return card(`
    <div class="p-5">
      ${sectionTitle("Öğretmen / Çocuk Oranı", sorunlu.length
        ? `<span class="text-[11px] font-bold rounded-full ${list.some(x => x.ihlal) ? "bg-coral text-white" : "bg-sun-soft text-[#B07E14]"} px-2.5 py-1">${sorunlu.length} sınıf</span>`
        : `<span class="text-[11px] font-bold rounded-full bg-teal-soft text-teal px-2.5 py-1">Uyumlu</span>`)}
      <p class="text-[11px] text-inksoft mb-3">
        Sınıf tanımındaki oran ile atanmış öğretmen sayısı karşılaştırılır.
        İhlal denetimde sorun; kapasite riski sınıf dolduğunda ihlale döner.
      </p>

      <div class="overflow-x-auto">
        <table class="w-full text-sm min-w-[480px]">
          <thead><tr class="text-left text-[11px] uppercase tracking-wide text-inksoft border-b border-line">
            <th class="py-2 pr-3">Sınıf</th>
            <th class="py-2 pr-3">Oran</th>
            <th class="py-2 pr-3 text-center">Öğretmen</th>
            <th class="py-2 pr-3 text-center">Bugün</th>
            <th class="py-2">Durum</th>
          </tr></thead>
          <tbody class="divide-y divide-line/70">
            ${list.map(x => `
              <tr class="${x.ihlal ? "bg-coral-soft/40" : ""}">
                <td class="py-2.5 pr-3">${classChip(x.sinif.id)}</td>
                <td class="py-2.5 pr-3 text-inksoft whitespace-nowrap">1 / ${x.gerekenOran ?? "?"}</td>
                <td class="py-2.5 pr-3 text-center font-semibold">${x.ogretmen}</td>
                <td class="py-2.5 pr-3 text-center">${x.bugun}<span class="text-[10px] text-inksoft"> / ${x.sinif.capacity}</span></td>
                <td class="py-2.5 whitespace-nowrap">
                  ${x.ihlal
                    ? `<span class="text-[10px] font-bold rounded-full bg-coral text-white px-2 py-0.5">İhlal · ${x.eksikOgretmen} eksik</span>`
                    : x.kapasiteRiski
                      ? `<span class="text-[10px] font-bold rounded-full bg-sun-soft text-[#B07E14] px-2 py-0.5">Kapasitede ${x.kapasiteIhtiyac} gerekir</span>`
                      : `<span class="text-[10px] font-bold rounded-full bg-teal-soft text-teal px-2 py-0.5">Uyumlu</span>`}
                </td>
              </tr>`).join("")}
          </tbody>
        </table>
      </div>
    </div>`, "min-w-0");
}

// ---------- Doluluk ----------
function dolulukKarti(bid) {
  const trend = dolulukTrendi(bid);
  const enYuksek = Math.max(...trend.map(x => x.oran), 1);
  const maliyet = bosKontenjanMaliyeti(bid);

  return card(`
    <div class="p-5">
      ${sectionTitle("Doluluk Eğilimi", `<span class="text-[11px] text-inksoft">son ${trend.length} dönem</span>`)}
      <div class="flex items-end gap-1.5 h-28 mb-2">
        ${trend.map(x => `
          <div class="flex-1 flex flex-col items-center gap-1 min-w-0">
            <span class="text-[10px] font-semibold">%${x.oran}</span>
            <div class="w-full rounded-t bg-teal" style="height:${Math.max(4, (x.oran / enYuksek) * 80)}px"
              title="${x.donem}: ${x.kayitli}/${x.kapasite}"></div>
            <span class="text-[9px] text-inksoft truncate w-full text-center">${x.donem.slice(5)}</span>
          </div>`).join("")}
      </div>

      <div class="mt-3 pt-3 border-t border-line">
        <div class="text-[11px] font-bold text-inksoft uppercase mb-1.5">Boş kontenjanın maliyeti</div>
        ${maliyet.filter(x => x.bos > 0).map(x => `
          <div class="flex items-center justify-between gap-2 text-xs py-0.5">
            <span class="truncate">${esc(x.sinif.name)} · ${x.bos} boş</span>
            <span class="shrink-0 font-semibold text-coral">${money(x.kayipGelir)}/ay</span>
          </div>`).join("") || `<div class="text-xs text-inksoft">Tüm sınıflar dolu.</div>`}
      </div>
    </div>`, "min-w-0");
}

// ---------- Tahsilat ----------
function tahsilatKarti(bid) {
  const trend = tahsilatTrendi(bid);

  return card(`
    <div class="p-5">
      ${sectionTitle("Tahsilat Oranı")}
      <div class="space-y-2">
        ${trend.map(x => `
          <div>
            <div class="flex items-center justify-between gap-2 text-xs">
              <span>${donemAdi(x.donem)}</span>
              <span class="shrink-0">
                <span class="font-semibold">${x.oran == null ? "—" : `%${x.oran}`}</span>
                ${x.ortGecikme ? `<span class="text-[10px] text-inksoft"> · ort. ${x.ortGecikme}g gecikme</span>` : ""}
              </span>
            </div>
            <div class="h-1.5 rounded-full bg-line mt-1 overflow-hidden">
              <div class="h-full rounded-full ${(x.oran ?? 0) >= 90 ? "bg-teal" : (x.oran ?? 0) >= 70 ? "bg-sun" : "bg-coral"}"
                style="width:${x.oran ?? 0}%"></div>
            </div>
          </div>`).join("")}
      </div>
      <p class="text-[11px] text-inksoft mt-3">
        İçinde bulunulan dönem henüz kapanmadığı için oranı düşük görünür.
      </p>
    </div>`, "min-w-0");
}

// ---------- Yaşlandırma ----------
function yaslandirmaKarti(bid) {
  const kovalar = bakiyeYaslandirma(bid);
  const toplam = kovalar.reduce((a, k) => a + k.tutar, 0);

  return card(`
    <div class="p-5">
      ${sectionTitle("Açık Bakiye Yaşlandırma", `<span class="text-[11px] font-bold">${money(toplam)}</span>`)}
      <p class="text-[11px] text-inksoft mb-3">Muhasebenin standart raporu — vadesine göre kırılım.</p>
      ${kovalar.map(k => {
        const oran = toplam ? Math.round((k.tutar / toplam) * 100) : 0;
        const renk = k.id === "vadesi_gelmemis" ? "sky" : k.id === "0_30" ? "sun" : "coral";
        return `
        <div class="mb-2">
          <div class="flex items-center justify-between gap-2 text-xs">
            <span>${esc(k.label)} <span class="text-inksoft">${k.adet} fatura</span></span>
            <span class="shrink-0 font-semibold">${money(k.tutar)}</span>
          </div>
          <div class="h-1.5 rounded-full bg-line mt-1 overflow-hidden">
            <div class="h-full rounded-full bg-${renk}" style="width:${oran}%"></div>
          </div>
        </div>`;
      }).join("")}
    </div>`, "min-w-0");
}

// ---------- Huni ----------
function huniKarti(bid) {
  const h = huniDonusumu(bid);

  return card(`
    <div class="p-5">
      ${sectionTitle("Başvuru Kaynakları", `<span class="text-[11px] text-inksoft hidden sm:block">Hangi kanal kayıt getiriyor?</span>`)}
      <div class="overflow-x-auto">
        <table class="w-full text-sm min-w-[420px]">
          <thead><tr class="text-left text-[11px] uppercase tracking-wide text-inksoft border-b border-line">
            <th class="py-2 pr-3">Kaynak</th>
            <th class="py-2 pr-3 text-center">Başvuru</th>
            <th class="py-2 pr-3 text-center">Kayıt</th>
            <th class="py-2 pr-3 text-center">Kayıp</th>
            <th class="py-2">Dönüşüm</th>
          </tr></thead>
          <tbody class="divide-y divide-line/70">
            ${h.kaynaklar.map(k => `
              <tr>
                <td class="py-2.5 pr-3 truncate">${esc(k.kaynak)}</td>
                <td class="py-2.5 pr-3 text-center">${k.toplam}</td>
                <td class="py-2.5 pr-3 text-center text-teal font-semibold">${k.kayit}</td>
                <td class="py-2.5 pr-3 text-center text-coral">${k.kayip}</td>
                <td class="py-2.5">
                  ${k.donusum == null
                    ? `<span class="text-[11px] text-inksoft">sonuçlanan yok</span>`
                    : `<span class="font-semibold">%${k.donusum}</span>`}
                </td>
              </tr>`).join("")}
          </tbody>
        </table>
      </div>

      <div class="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-line">
        <div>
          <div class="text-[11px] font-bold text-inksoft uppercase mb-1">Aşamada bekleme</div>
          ${h.asamalar.filter(a => a.adet).map(a => `
            <div class="flex items-center justify-between gap-2 text-[11px] py-0.5">
              <span class="truncate">${esc(a.asama.label)} (${a.adet})</span>
              <span class="shrink-0 ${a.enUzun > 14 ? "text-coral font-semibold" : "text-inksoft"}">ort. ${a.ortBekleme}g</span>
            </div>`).join("")}
        </div>
        <div>
          <div class="text-[11px] font-bold text-inksoft uppercase mb-1">Kayıp nedenleri</div>
          ${h.kayipNedenleri.length ? h.kayipNedenleri.map(k => `
            <div class="flex items-center justify-between gap-2 text-[11px] py-0.5">
              <span class="truncate">${esc(k.neden)}</span>
              <span class="shrink-0 font-semibold">${k.adet}</span>
            </div>`).join("") : `<div class="text-[11px] text-inksoft">Kayıp yok.</div>`}
        </div>
      </div>
    </div>`, "min-w-0");
}

// ---------- Devamsızlık ----------
function devamsizlikKarti(bid) {
  const list = devamsizlikDeseni(bid).slice(0, 8);

  return card(`
    <div class="p-5">
      ${sectionTitle("Devamsızlık Deseni", `<span class="text-[11px] text-inksoft">${donemAdi(DONEM)}</span>`)}
      <p class="text-[11px] text-inksoft mb-3">
        Sürekli devamsızlık ayrılma sinyalidir — veliyle konuşulmalı.
      </p>
      ${list.length ? `
        <ul class="space-y-1.5">
          ${list.map(x => `
            <li data-student="${x.student.id}" class="flex items-center gap-3 rounded-xl border ${(x.devamOrani ?? 100) < 80 ? "border-coral" : "border-line"} px-3 py-2 cursor-pointer hover:bg-paper/60">
              ${avatar(x.student, "w-7 h-7")}
              <span class="min-w-0 flex-1">
                <span class="block text-sm font-semibold truncate">${esc(x.student.name)}</span>
                <span class="block text-[11px] text-inksoft">
                  ${x.geldi} geldi · ${x.izinli} izinli · ${x.gelmedi} bildirimsiz
                </span>
              </span>
              <span class="shrink-0 font-display font-bold ${(x.devamOrani ?? 100) < 80 ? "text-coral" : ""}">
                %${x.devamOrani ?? "—"}
              </span>
            </li>`).join("")}
        </ul>` : emptyNote("Devam verisi yok.")}
    </div>`, "min-w-0");
}

// ---------- Devamlılık ----------
function devamlilikKarti(bid) {
  const d = devamlilik(bid);
  const toplam = d.kovalar.reduce((a, k) => a + k.adet, 0);

  return card(`
    <div class="p-5">
      ${sectionTitle("Öğrenci Devamlılığı", `<span class="text-[11px] text-inksoft">ort. ${d.ortalamaYil} yıl</span>`)}
      ${d.kovalar.map(k => {
        const oran = toplam ? Math.round((k.adet / toplam) * 100) : 0;
        return `
        <div class="mb-2">
          <div class="flex items-center justify-between gap-2 text-xs">
            <span>${esc(k.label)}</span>
            <span class="shrink-0 font-semibold">${k.adet} <span class="text-inksoft font-normal">%${oran}</span></span>
          </div>
          <div class="h-1.5 rounded-full bg-line mt-1 overflow-hidden">
            <div class="h-full rounded-full bg-grape" style="width:${oran}%"></div>
          </div>
        </div>`;
      }).join("")}

      <div class="rounded-xl bg-teal-soft p-3 mt-3 text-xs">
        <span class="font-bold text-teal">Kardeş oranı %${d.kardesOrani.oran}</span>
        <span class="text-inksoft"> — ${d.kardesOrani.veli} veliden ${d.kardesOrani.cokCocuklu} tanesinin
        birden fazla çocuğu burada. Bu en güçlü memnuniyet göstergesidir.</span>
      </div>
    </div>`, "min-w-0");
}
